import { CurrencyPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { AppConfig } from '../../app.config';
import { IDomainInfo } from '../../models/domaininfo';
import { IOutage } from '../../models/outage';
import { CreditCardIssuerType } from '../../models/paymentmethod';
import { IPaymentPlanBalance } from '../../models/paymentplanbalance';
import { IPaymentPlanTermsAndConditions } from '../../models/paymentplantermsandconditions';
import { Roles } from '../../models/roles';
import { DateFormatPipe } from '../../pipes/DateFormat/dateformat.pipe';
import { ArchiveService } from '../archive/archive.service';
import { DomainContentItemsService } from '../contentItems/domaincontentitems.service';
import { DomainInfoService } from '../domainInfo/domaininfo.service';
import { LoggingLevel, LoggingService } from '../logging/logging.service';
import { StorageService } from '../storage/storage.service';
import { ValidationService } from '../validation/validation.service';

@Injectable()
export class ComponentService {

    isForgotUsernameOrPasswordResetOpen = false;
    forgotUsernameAndPasswordResetChange: Subject<boolean> = new Subject<boolean>();

    // This is set inside autopayagreement.component.ts and used here to determine
    // if the Consumer has changed the MayPaymentAmount on their AutoPay agreement
    // and created an amount that conflicts with their MP's MaxCCPaymentAmount.
    isAutoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmount = false;
    autoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmountChange: Subject<boolean> = new Subject<boolean>();

    maxCCPaymentAmount = 0;
    maxCCPaymentAmountChange: Subject<number> = new Subject<number>();

    isFuturePaymentDateValid = false;
    futurePaymentDateValidChange: Subject<boolean> = new Subject<boolean>();

    // We need to hold this detail information for Deviceless Future Dated Payments.
    payableEntryOrItemBalances: IPaymentPlanBalance[] = [];
    payableEntryOrItemBalancesChange: Subject<IPaymentPlanBalance[]> = new Subject<IPaymentPlanBalance[]>();

    profileDeliveryMethodOptions: string;
    profileDeliveryMethodOptionsChange: Subject<string> = new Subject<string>();

    constructor(private http: HttpClient, private config: AppConfig, private archive: ArchiveService,
                private storage: StorageService, private domain: DomainInfoService, private content: DomainContentItemsService,
                private logging: LoggingService, private validation: ValidationService, private sanitizer: DomSanitizer,
                private router: Router, private currencyPipe: CurrencyPipe, private datePipe: DateFormatPipe,
                private ngZone: NgZone) {
        this.archiveService = archive;
        this.domainService = domain;
        this.contentService = content;
        this.storageService = storage;
        this.loggingService = logging;
        this.validationService = validation;

        this.contentService.content$.subscribe(c => {
            this.errors = c.error;
        });

        this.forgotUsernameAndPasswordResetChange.subscribe((value) => {
            this.isForgotUsernameOrPasswordResetOpen = value;
        });

        this.autoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmountChange.subscribe((value) => {
            this.isAutoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmount = value;
        });

        this.maxCCPaymentAmountChange.subscribe((value) => {
            this.maxCCPaymentAmount = value;
        });

        this.futurePaymentDateValidChange.subscribe((value) => {
            this.isFuturePaymentDateValid = value;
        });

        this.payableEntryOrItemBalancesChange.subscribe((value) => {
            this.payableEntryOrItemBalances = value;
        });

        this.profileDeliveryMethodOptionsChange.subscribe((value) => {
            this.profileDeliveryMethodOptions = value;
        });
    }

    public archiveService: ArchiveService;
    public domainService: DomainInfoService;
    public contentService: DomainContentItemsService;
    public storageService: StorageService;
    public loggingService: LoggingService;
    public validationService: ValidationService;
    private errors: any;
    readonly emailRegex = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    readonly NULL_GUID = '00000000-0000-0000-0000-000000000000';
    readonly phoneMask: Array<string | RegExp> = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

    public static toDecimal(inValue: number, decimalPlaces: number = 2): number {
        if (typeof inValue !== 'number') { // catches null & undefined
            return 0;
        }
        const multiplier = Math.pow(10, decimalPlaces);

        // Because of very weird edge case javascript rounding issues. Without the 0.000001 rounding
        // 1.005 to 2 decimals would return 1 instead of 1.01. A unit test was added.
        return Math.round((inValue + 0.0000001) * multiplier) / multiplier;
    }

    public static sortDateAscending(a: Date, b: Date): number {
        if (a > b) { return 1; }
        if (a < b) { return -1; }
        return 0;
    }

    public static sortDateDescending(a: Date, b: Date): number {
        if (a > b) { return -1; }
        if (a < b) { return 1; }
        return 0;
    }

    /**
     * Parses the given validation error and returns a more readable string
     *
     * @param {string} error
     * @returns {string}
     *
     * @memberof ComponentService
     */
    public handleValidationErrorCode(error: string): string {
        const split: string[] = error.split(':');
        if (split.length !== 2) {
            return error;
        }

        const errorCode: string = split[0].split('.')[0];
        const errorSubCode: string = split[0].replace('.', '');

        let contentItem: string = this.errors['errorCode' + errorCode]['error' + errorSubCode].text;

        const replaceValue = error.substring(error.indexOf('!') + 1, error.indexOf('!', error.indexOf('!') + 1));
        if (replaceValue.length > 0) {
            contentItem = contentItem.replace('!!', replaceValue);
        }

        return contentItem;
    }

    public userIsLoggedIn(): boolean {
        const userIsLoggedIn = this.storageService.exists('token');
        this.loggingService.log('ComponentService->userIsLoggedIn->' + userIsLoggedIn, LoggingLevel.debug);
        return userIsLoggedIn;
    }

    quickpayUserIsLoggedIn(): boolean {
        const tokenExists = this.storageService.exists('token');
        const memCodeExists = this.storageService.exists('mem');

        return tokenExists && memCodeExists;
    }

    public isMobileBrowser(): boolean {
        // Super complicated  if needed later
        // tslint:disable-next-line: max-line-length
        // /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4))
        return /Mobi/.test(navigator.userAgent);
    }

    public isIE(): boolean {
        return navigator.userAgent.indexOf('Trident') >= 0;
    }

    public detectBrowser(): string {
        if (this.isIE()) {
            return 'Internet Explorer';
        } else if (navigator.userAgent.indexOf('Edge') >= 0) {
            return 'Edge';
        } else if (navigator.userAgent.indexOf('Chrome') >= 0) {
            return 'Chrome';
        } else if (navigator.userAgent.indexOf('Firefox') >= 0) {
            return 'Firefox';
        } else if (navigator.userAgent.indexOf('Safari') >= 0) {
            return 'Safari';
        } else {
            return 'Unknown';
        }
    }

    /**
     * This will show that the domain is an SSO domain.
     *
     * @param {IDomainInfo} domainInfo
     * @returns {boolean}
     * @memberof ComponentService
     */
    public isSSO(domainInfo: IDomainInfo): boolean {
        if (!!domainInfo && !!domainInfo.domainMode && domainInfo.domainMode.includes('SSO')) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Return a RFC4122 version 4 compliant UUID
     * Used through public domain license
     * https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
     *
     * @returns {string}
     * @memberof ComponentService
     */
    public generateUUID(): string { // Public Domain/MIT
        let d = new Date().getTime();
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            d += performance.now(); // use high-precision timer if available
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            // tslint:disable-next-line: no-bitwise
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            // tslint:disable-next-line: no-bitwise
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    /**
     * Converts base64 encoded file bytestreams into Blobs for downloading
     * Stolen from https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
     *
     * @param {*} b64Data
     * @param {string} contentType
     * @param {number} [sliceSize=512]
     * @returns
     * @memberof ComponentService
     */
    public b64ToBlob(b64Data: any, contentType: string, sliceSize: number = 512) {
        contentType = contentType || '';
        sliceSize = sliceSize || 512;

        const byteCharacters = atob(b64Data);
        const byteArrays: any[] = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

    public scrollPageToTop(): void {
        window.scrollTo(0, 0);
    }

    public monthDiff(d1: Date, d2: Date) {
        const displayTo = new Date(d2);
        const displayFrom = new Date(d1);
        return (displayTo.getMonth() - displayFrom.getMonth() + (12 * (displayTo.getFullYear() - displayFrom.getFullYear()))) + 1;
    }

    /**
     * Takes in a list of <T> and creates a new Set of it. Because Sets don't allow duplicates, only the unique elements are returned.
     *
     * @param {any[]} itemlist
     * @returns {any[]}
     * @memberof ComponentService
     */
    public removeDuplicatesFromList(itemlist: any[]): any[] {
        return Array.from(new Set(itemlist));
    }

    /**
     * An implementation of Thread.sleep() that can be used to test lazy loading
     *
     * @param {number} time
     * @returns
     * @memberof ComponentService
     */
    public async lazyLoadTest(time: number): Promise<{}> {
        return new Promise((resolve) => setTimeout(resolve, time));
    }

    public letMeIn(): boolean {
        this.loggingService.log('returning letmein:' + this.storageService.exists('letmein'), LoggingLevel.debug);
        return this.storageService.exists('letmein');
    }

    /**
     * An implementation to sort an array of objects by a string property.
     * Example: let sortedListByStringProperty =
     *      [ { id: 1, description: 'z' },
     *      { id: 2, description: 'a' } ].sort(sortListByStringProperty('description'));
     *
     * @param {string} property
     * @returns {any[]}
     * @memberof ComponentService
     */
    public sortListByStringProperty(property: string) {
        return (a: any, b: any) => {
            let updatedSortPosition = 0;

            if (a[property].toUpperCase() < b[property].toUpperCase()) {
                updatedSortPosition = -1;
            } else if (a[property].toUpperCase() > b[property].toUpperCase()) {
                updatedSortPosition = 1;
            }
            return updatedSortPosition;
        };
    }

    /**
     * Security Risk https://angular.io/api/platform-browser/DomSanitizer
     * DomSanitizer helps prevent Cross Site Scripting Security bugs (XSS) by sanitizing values
     * to be safe to use in the different DOM contexts.
     * Calling bypassSecurityTrustHtml disables Angular's built-in sanitization for the value passed in.
     * Carefully check and audit all values and code paths going into this call.
     *
     * @param {string} content
     * @returns {SafeHtml}
     * @memberof ComponentService
     */
    public BypassSecurityTrustHtml(content: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(content);
    }

    public getLogoByCard(cardNumber: string): CreditCardIssuerType {
        const accountNumber: string = cardNumber;
        let creditCardIssuer: CreditCardIssuerType = null;

        if (accountNumber.toString().length >= 4) {
            const visaRegex = new RegExp('^4[0-9]{6,}$');
            const mastercardRegex = new RegExp('^5[1-5][0-9]{5,}|222[1-9][0-9]{3,}|22[3-9][0-9]{4,}|2[3-6][0-9]{5,}|27[01][0-9]{4,}|2720[0-9]{3,}$');
            const americanexpressRegex = new RegExp('^3[47][0-9]{5,}$');
            const dinersClubRegex = new RegExp('^3(?:0[0-5]|[68][0-9])[0-9]{4,}$');
            const discoverRegex = new RegExp('^6(?:011|5[0-9]{2})[0-9]{3,}$');
            const jcbRegex = new RegExp('^(?:2131|1800|35[0-9]{3})[0-9]{3,}$');

            if (visaRegex.test(accountNumber.toString())) {
                creditCardIssuer = CreditCardIssuerType.visa;
            } else if (americanexpressRegex.test(accountNumber.toString())) {
                creditCardIssuer = CreditCardIssuerType.americanexpress;
            } else if (dinersClubRegex.test(accountNumber.toString())) {
                creditCardIssuer = CreditCardIssuerType.dinersclub;
            } else if (discoverRegex.test(accountNumber.toString())) {
                creditCardIssuer = CreditCardIssuerType.discover;
            } else if (jcbRegex.test(accountNumber.toString())) {
                creditCardIssuer = CreditCardIssuerType.jcb;
            } else if (mastercardRegex.test(accountNumber.toString())) {
                creditCardIssuer = CreditCardIssuerType.mastercard;
            }
        }
        return creditCardIssuer;
    }

    /**
     * Returns the DomainInfo ExternalPaymentUrl field if it exists, otherwise returns /payment
     *
     * @returns {Promise<string>}
     * @memberof ComponentService
     */
    public getPaymentUrlForDomain(): Promise<string> {
        return new Promise(resolve => {
            this.domainService.domainInfo$.subscribe((domainInfo: IDomainInfo) => {
                if (domainInfo != null && domainInfo.externalPaymentUrl != null && domainInfo.externalPaymentUrl.trim() !== '') {
                    resolve(domainInfo.externalPaymentUrl);
                } else {
                    resolve('/payment');
                }
            }, () => {
                // if error, assume /payment
                this.loggingService.log('ComponentService -> getPaymentUrlForDomain() -- Unable to retrieve domain info external payment URL', LoggingLevel.error);
                resolve('/payment');
            }).unsubscribe();
        });
    }

    /**
     * Redirects via router or new tab to the configured payment URL for the domain
     *
     * @memberof ComponentService
     */
    public redirectToPayment(): void {
        this.getPaymentUrlForDomain().then(url => {
            if (url.startsWith('/')) {
                this.ngZone.run(() => this.router.navigateByUrl(url)).then();
            } else {
                window.open(url, '_blank');
            }
        });
    }

    /**
     * Checks for the existance of the role in the role array.
     *
     * @param {Roles} role  the role to investigate
     * @memberof ComponentService
     */
    public hasRole(role: Roles) {
        if (this.storageService.exists('roles')) {
            const roles: string[] = this.storageService.retrieve('roles');
            return roles.indexOf(Roles[role]) >= 0;  // role exists
        } else {
            return false;
        }
    }

    isValidGUID(guid: string): boolean {
        return !!guid && guid !== this.NULL_GUID;
    }

    setForgotUsernameAndPasswordReset(isOpen: boolean) {
        this.forgotUsernameAndPasswordResetChange.next(isOpen);
    }

    setAutoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmount(autoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmount: boolean) {
        this.autoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmountChange.next(autoPayMaxPaymentAmountGreaterThanMaxCCPaymentAmount);
    }

    setMaxCCPaymentAmount(maxCCPaymentAmount: number) {
        this.maxCCPaymentAmountChange.next(maxCCPaymentAmount);
    }

    setFuturePaymentDateValid(futurePaymentDateValid: boolean) {
        this.futurePaymentDateValidChange.next(futurePaymentDateValid);
    }

    setPayableEntryOrItemBalances(payableEntryOrItemBalances: IPaymentPlanBalance[]) {
        this.payableEntryOrItemBalancesChange.next(payableEntryOrItemBalances);
    }

    setProfileDeliveryMethodOptions(profileDeliveryMethodOptions: string) {
        this.profileDeliveryMethodOptionsChange.next(profileDeliveryMethodOptions);
    }

    public formatTermsAndConditions(termsAndConditionsContent: string, plan: IPaymentPlanTermsAndConditions): string {
        const message = 'Not available';
        return termsAndConditionsContent
            .replace('!DATE!', this.datePipe.transform(plan.createdDate, 'MM/DD/YYYY'))
            .replace('!BALANCE!', this.currencyPipe.transform(plan.balance, 'USD', 'symbol'))
            .replace('!PAYMENTAMOUNT!', plan.monthlyPaymentAmount !== undefined ? this.currencyPipe.transform(plan.monthlyPaymentAmount, 'USD', 'symbol') : message)
            .replace('!NEXTEXPECTEDPAYMENTAMOUNT!', this.currencyPipe.transform(plan.nextExpectedPaymentAmount, 'USD', 'symbol'))
            .replace('!NEXTEXPECTEDPAYMENTDATE!', this.datePipe.transform(plan.nextExpectedPaymentDate, 'MM/DD/YYYY'))
            .replace('!PAYMENTMETHOD!', plan.consumerPaymentMethod !== undefined ? plan.consumerPaymentMethod.description : message)
            .replace('!EMAIL!', plan.emailAddress || message)
            .replace('!NAME!', plan.name || message)
            .replace('!STARTINGBALANCE!', plan.startingBalance !== undefined ? this.currencyPipe.transform(plan.startingBalance, 'USD', 'symbol') : message)
            .replace('!DURATION!', plan.duration === undefined ? message : plan.duration.toString())
            .replace('!FIRSTPAYMENTAMOUNT!', this.currencyPipe.transform(plan.firstPaymentAmount, 'USD', 'symbol'))
            .replace('!FIRSTPAYMENTDATE!', this.datePipe.transform(plan.firstPaymentDate, 'MM/DD/YYYY'))
            .replace('!FINALPAYMENTAMOUNT!', this.currencyPipe.transform(plan.finalPaymentAmount, 'USD', 'symbol'))
            .replace('!FINALPAYMENTDATE!', this.datePipe.transform(plan.finalPaymentDate, 'MM/DD/YYYY'))
            .replace('!CREATEDBY!', plan.createdBy || message);
    }

    outageInStorage(): boolean {
        const outageList: IOutage[] = this.storage.retrieve('outages');
        const now = this.getNowWithOutSeconds();

        if (outageList &&
            outageList.some(x =>
                now >= new Date(x.outageStartDate) &&
                now < new Date(x.outageEndDate) &&
                x.system === 'MySecureBill Maintenance'
            )
        ) {
            if (!this.letMeIn()) {
                return true;
            }
        }

        return false;
    }

    getNowWithOutSeconds(): Date {
        // Somewhere logic is looking to round
        // up a minute, but not under my watch!
        const nowWithOutSeconds = new Date();
        nowWithOutSeconds.setSeconds(0, 0);

        return nowWithOutSeconds;
    }

    bucketCannotBeUnderPaid(
        bucketPaymentAmount: number,
        minPaymentAmount: number,
        allowShortPayDetailOverpayments: boolean
    ): boolean {
        if (bucketPaymentAmount >= minPaymentAmount) {
            return false;
        } else if (bucketPaymentAmount < minPaymentAmount && allowShortPayDetailOverpayments) {
            return false;
        }

        // bucketPaymentAmount is LESS THAN minPaymentAmount and
        // allowShortPayDetailOverpayments is FALSE, thus "bucketCannotBeUnderPaid"
        return true;
    }

    bucketCannotBeOverPaid(
        bucketPaymentAmount: number,
         maxPaymentAmount: number,
         detailOverpayments: boolean
    ): boolean {
        if (bucketPaymentAmount <= maxPaymentAmount) {
            return false;
        } else if (bucketPaymentAmount > maxPaymentAmount && detailOverpayments) {
            return false;
        }

        // bucketPaymentAmount is GREATER THAN maxPaymentAmount and
        // detailOverpayments is FALSE, thus "bucketCannotBeOverPaid"
        return true;
    }
}
